import { useEffect, useState } from "react";
import { FunnelX } from "lucide-react";

const FILTER_KEYS = {
  fullName: "fullName",
  subgroup: "subgroup",
  practical: "practical",
  report: "report",
  code: "code",
  reviewStatus: "reviewStatus",
};

export default function Filter({
  stats,
  setStatsFiltered,
  isIndividual,
  selectedStatus,
  statusesSummary,
  onSelectedStatusChange,
}) {
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterValues, setFilterValues] = useState([]);
  const [selectedFilterValue, setSelectedFilterValue] = useState(null);

  const availableFilters = [
    isIndividual
      ? { key: FILTER_KEYS.fullName, value: "ФИО" }
      : { key: FILTER_KEYS.subgroup, value: "Подгруппа" },
    { key: FILTER_KEYS.practical, value: "Практика" },
    { key: FILTER_KEYS.report, value: "Отчёт" },
    { key: FILTER_KEYS.code, value: "Исходный код" },
    { key: FILTER_KEYS.reviewStatus, value: "Статус проверки" },
  ];

  const resetSelectedFilterValue = () => {
    setSelectedFilterValue(null);
    onSelectedStatusChange(undefined);
  };

  const resetSelectedFilter = () => {
    resetSelectedFilterValue();
    setSelectedFilter(null);
  };

  useEffect(() => {
    setStatsFiltered(stats);
    resetSelectedFilter();
  }, [stats]);

  useEffect(() => {
    if (selectedStatus) {
      if (selectedFilter?.key === FILTER_KEYS.reviewStatus) {
        if (selectedFilterValue?.key === selectedStatus.code) {
          return;
        }
      } else {
        const filter = availableFilters.find(
          (item) => item.key === FILTER_KEYS.reviewStatus,
        );
        setSelectedFilter(filter);
      }

      setSelectedFilterValue({
        key: selectedStatus.code,
        value: selectedStatus.name,
      });
    } else {
      if (
        selectedFilter?.key === FILTER_KEYS.reviewStatus &&
        selectedFilterValue
      ) {
        setSelectedFilterValue(null);
      }
    }
  }, [selectedStatus]);

  useEffect(() => {
    switch (selectedFilter?.key) {
      case FILTER_KEYS.fullName:
        {
          const map = new Map();
          stats.forEach((s) => {
            if (!map.has(s.studentId)) {
              map.set(s.studentId, s.fullName);
            }
          });
          const res = Array.from(map).map((elem) => ({
            key: elem[0],
            value: elem[1],
          }));
          setFilterValues(res);
        }
        break;
      case FILTER_KEYS.subgroup:
        {
          const uniqueSubgroups = [
            ...new Set(stats.map((s) => s.subgroupNumber ?? 0)),
          ];
          const res = uniqueSubgroups.map((elem) => ({
            key: elem,
            value: `Подгруппа ${elem}`,
          }));
          setFilterValues(res);
        }
        break;
      case FILTER_KEYS.practical:
        {
          const uniquePracticals = [
            ...new Set(stats.map((s) => s.practicalNumber ?? 0)),
          ];
          const res = uniquePracticals.map((elem) => ({
            key: elem,
            value: `Практика ${elem}`,
          }));
          setFilterValues(res);
        }
        break;
      case FILTER_KEYS.report:
      case FILTER_KEYS.code:
        {
          const res = [
            {
              key: true,
              value: "размещён",
            },
            {
              key: false,
              value: "не размещён",
            },
          ];
          setFilterValues(res);
        }
        break;
      case FILTER_KEYS.reviewStatus:
        setFilterValues(
          statusesSummary?.map((elem) => ({
            key: elem.code,
            value: elem.name,
          })) ?? [],
        );
        break;
      default:
        setFilterValues([]);
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (!selectedFilterValue) {
      setStatsFiltered(stats);
      return;
    }

    switch (selectedFilter?.key) {
      case FILTER_KEYS.fullName:
        {
          const res = stats.filter(
            (elem) => elem.studentId === selectedFilterValue.key,
          );
          setStatsFiltered(res);
        }
        break;
      case FILTER_KEYS.subgroup:
        {
          const res = stats.filter(
            (elem) => elem.subgroupNumber === selectedFilterValue.key,
          );
          setStatsFiltered(res);
        }
        break;
      case FILTER_KEYS.practical:
        {
          const res = stats.filter(
            (elem) => elem.practicalNumber === selectedFilterValue.key,
          );
          setStatsFiltered(res);
        }
        break;
      case FILTER_KEYS.report:
        {
          const func = selectedFilterValue.key
            ? (elem) => elem.reportLink
            : (elem) => !elem.reportLink;
          const res = stats.filter(func);
          setStatsFiltered(res);
        }
        break;
      case FILTER_KEYS.code:
        {
          const func = selectedFilterValue.key
            ? (elem) => elem.codeLink
            : (elem) => !elem.codeLink;
          const res = stats.filter(func);
          setStatsFiltered(res);
        }
        break;
      case FILTER_KEYS.reviewStatus:
        {
          const res = stats.filter(
            (elem) => elem.reviewStatus?.code === selectedFilterValue.key,
          );
          setStatsFiltered(res);
        }
        break;
    }
  }, [selectedFilterValue]);

  return (
    <fieldset>
      <legend className="muted">Фильтрация</legend>
      <div className="filters">
        <select
          value={selectedFilter?.value ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            const selectedKey =
              availableFilters.find((elem) => elem.value === value)?.key ?? "";
            setSelectedFilter({ key: selectedKey, value });
            resetSelectedFilterValue();
          }}
        >
          <option value="">Выберите столбец</option>
          {availableFilters.map((f) => (
            <option key={f.key} value={f.value}>
              {f.value}
            </option>
          ))}
        </select>
        <select
          value={selectedFilterValue?.value ?? ""}
          disabled={!selectedFilter}
          onChange={(e) => {
            let selectedKey;
            let newSelectedValue = null;
            const value = e.target.value;

            if (value !== "") {
              selectedKey = filterValues.find(
                (elem) => elem.value === e.target.value,
              )?.key;
              newSelectedValue = { key: selectedKey, value };
            }
            setSelectedFilterValue(newSelectedValue);

            if (selectedFilter?.key === FILTER_KEYS.reviewStatus)
              onSelectedStatusChange(selectedKey);
          }}
        >
          <option value="">Выберите значение</option>
          {filterValues.map((f) => (
            <option key={f.key} value={f.value}>
              {f.value}
            </option>
          ))}
        </select>
        <button
          className="btn small"
          disabled={!selectedFilter}
          title="Сбросить фильтр"
          onClick={resetSelectedFilter}
        >
          <FunnelX size={20} />
        </button>
      </div>
    </fieldset>
  );
}
